import { Readability } from '@mozilla/readability';
import TurndownService from 'turndown';

import { fetchTextResilient } from '@/lib/resilientFetch';

/**
 * Web page → readable article extraction, for the in-app web reader.
 *
 * Pipeline: fetch HTML (resiliently) → DOMParser (never executes scripts) →
 * Mozilla Readability (reader-mode extraction) → Turndown (HTML → markdown).
 * The markdown is rendered with react-markdown, which drops raw HTML by
 * default — so untrusted page markup never reaches the DOM.
 */

export interface ExtractedArticle {
  title: string;
  byline?: string;
  excerpt?: string;
  siteName?: string;
  markdown: string;
}

const MAX_HTML_BYTES = 2 * 1024 * 1024;

/** Fetch and extract a readable article from a web page. Null on failure. */
export async function extractArticle(url: string): Promise<ExtractedArticle | null> {
  const html = await fetchTextResilient(url, 12_000, MAX_HTML_BYTES);
  if (!html) return null;

  const doc = new DOMParser().parseFromString(html, 'text/html');

  // DOMParser documents inherit this app's URL, which would corrupt relative
  // link/image resolution. Point the base at the source page first.
  const base = doc.createElement('base');
  base.href = url;
  doc.head.prepend(base);

  const article = new Readability(doc).parse();
  if (!article?.content) return null;

  const turndown = new TurndownService({
    headingStyle: 'atx',
    bulletListMarker: '-',
    codeBlockStyle: 'fenced',
    emDelimiter: '*',
  });

  const markdown = turndown.turndown(article.content).trim();
  if (markdown.length < 100) return null; // extraction produced nothing readable

  return {
    title: article.title || new URL(url).hostname,
    byline: article.byline ?? undefined,
    excerpt: article.excerpt ?? undefined,
    siteName: article.siteName ?? undefined,
    markdown,
  };
}
